/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Mic, Square, Sparkles, Save, FileDown, RotateCcw, StopCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import useWebSpeech from '@/lib/useWebSpeech';
import { localLLM } from '@/lib/localLLM';
import { saveMedicalRecordOffline, listMedicalRecordsOffline } from '@/lib/offlineDb';
import { syncEngine } from '@/lib/syncEngine';
import { listClinicalNotes } from '@/lib/telemedicine';
import { downloadFhirBundle, triageToTranscript } from '@/components/clinical/record/clinicalActions';
import { SOAP_FIELDS, emptySoap, formatSoapText } from '@/components/clinical/record/clinicalConstants';
import {
  WaveBars, PanelToolbar, ClinicalPatientHeader, ClinicalNotesHistory, EmptyRecordState,
} from '@/components/clinical/record/ClinicalRecordParts';

/**
 * ClinicalRecordPanel — pannello "Cartella clinica" del Command Center.
 *
 * Flusso a clic minimi:
 *   1) [Detta] → trascrizione vocale live (Web Speech, on-device)
 *   2) [Genera SOAP] → AI locale in streaming compila S/O/A/P
 *   3) [Salva] → salvataggio ISTANTANEO offline (IndexedDB) + sync automatica
 *
 * Nessun dato lascia il dispositivo finché non c'è rete; nessun cloud AI.
 *
 * Refactor note (Code Quality): costanti e parti presentazionali
 * (toolbar, header paziente/triage, storico note) vivono in
 * `components/clinical/record/*`.
 */

export const ClinicalRecordPanel = ({ appt, onOpenRoom, authorId }) => {

  const [transcript, setTranscript] = useState('');
  const [soap, setSoap] = useState(emptySoap());
  const [stream, setStream] = useState('');
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [aiProvider, setAiProvider] = useState(null);
  const abortRef = useRef(null);
  const streamBoxRef = useRef(null);

  const speech = useWebSpeech({
    lang: 'it-IT',
    continuous: true,
    interim: true,
    onFinal: (chunk) => setTranscript((prev) => `${prev}${prev && !prev.endsWith(' ') ? ' ' : ''}${chunk}`),
  });

  // Reset dell'editor quando cambia paziente/appuntamento
  useEffect(() => {
    setTranscript(''); setSoap(emptySoap()); setStream(''); setAiProvider(null);
    speech.reset();
    if (speech.listening) speech.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appt?.id]);

  const loadHistory = useCallback(async () => {
    if (!appt?.id) { setHistory([]); return; }
    setHistoryLoading(true);
    try {
      const [remote, local] = await Promise.all([
        listClinicalNotes(appt.id).then((r) => r.notes || []).catch(() => []),
        listMedicalRecordsOffline({ appointment_id: appt.id }).catch(() => []),
      ]);
      const localPending = local.filter((l) => l.sync_status !== 'synced').map((l) => ({
        id: l.id, created_at: l.created_at, notes: formatSoapText(l.soap), diagnosis: l.soap?.assessment, local: true,
      }));
      const merged = [...localPending, ...remote].sort((a, b) => (a.created_at < b.created_at ? 1 : -1));
      setHistory(merged);
    } finally {
      setHistoryLoading(false);
    }
  }, [appt?.id]);

  useEffect(() => { loadHistory(); }, [loadHistory]);

  // Auto-scroll del box streaming
  useEffect(() => {
    if (streamBoxRef.current) streamBoxRef.current.scrollTop = streamBoxRef.current.scrollHeight;
  }, [stream]);

  const hasSoap = useMemo(() => Object.values(soap).some((v) => v && v.trim()), [soap]);

  const generate = async () => {
    const text = transcript.trim();
    if (!text) { toast.error('Detta o digita prima la trascrizione'); return; }
    if (speech.listening) speech.stop();
    setGenerating(true); setStream('');
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const res = await localLLM.generateSoap(text, {
        signal: ctrl.signal,
        onChunk: (partial, partialSoap) => {
          setStream(partial);
          if (partialSoap) setSoap((prev) => ({ ...prev, ...partialSoap }));
        },
      });
      setSoap(res.soap);
      setAiProvider(res.provider);
      toast.success(`SOAP generato (${res.latencyMs} ms · ${res.provider})`);
    } catch (e) {
      if (e?.name !== 'AbortError') toast.error('Generazione SOAP non riuscita');
    } finally {
      setGenerating(false);
      abortRef.current = null;
    }
  };

  const cancelGenerate = () => abortRef.current?.abort();

  const save = async () => {
    if (!hasSoap) { toast.error('Compila o genera almeno una sezione SOAP'); return; }
    setSaving(true);
    try {
      const rec = await saveMedicalRecordOffline({
        appointment_id: appt?.id || null,
        patient_id: appt?.patient_id || null,
        patient_name: appt?.patient_name || null,
        author_id: authorId || null,
        transcript,
        soap,
        ai_provider: aiProvider,
      });
      toast.success(navigator.onLine ? 'Cartella salvata · sincronizzazione in corso' : 'Cartella salvata offline · verrà sincronizzata al ritorno della rete');
      setTranscript(''); setSoap(emptySoap()); setStream(''); setAiProvider(null); speech.reset();
      // Tentativo immediato di sync (non bloccante)
      syncEngine.flush().then(loadHistory);
      setHistory((prev) => [{ id: rec.id, created_at: rec.created_at, notes: formatSoapText(rec.soap), diagnosis: rec.soap.assessment, local: true }, ...prev]);
    } catch (e) {
      toast.error('Salvataggio locale non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const exportFhir = async () => {
    if (!hasSoap || !appt) return;
    setExporting(true);
    try {
      await downloadFhirBundle(appt, soap);
      toast.success('Bundle FHIR R4 esportato');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Export FHIR non disponibile offline');
    } finally {
      setExporting(false);
    }
  };

  if (!appt) return <EmptyRecordState />;

  const importTriage = (triage) => setTranscript((p) => triageToTranscript(p, triage));

  return (
    <div className="h-full flex flex-col" data-testid="clinical-record-panel">
      <PanelToolbar />

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-4">
          <ClinicalPatientHeader appt={appt} onOpenRoom={onOpenRoom} onImportTriage={importTriage} />

          {/* Step 1 — Dettatura */}
          <section className="space-y-2" data-testid="clinical-dictation">
            <div className="flex items-center justify-between gap-2">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">1 · Trascrizione (on-device)</Label>
              <WaveBars active={speech.listening} />
            </div>
            <div className="flex items-center gap-2">
              {speech.supported ? (
                <Button
                  onClick={() => (speech.listening ? speech.stop() : speech.start())}
                  variant={speech.listening ? 'destructive' : 'default'}
                  className={cn('relative touch-target', speech.listening && 'rec-ring')}
                  data-testid="clinical-dictate-btn"
                  aria-pressed={speech.listening}
                >
                  {speech.listening ? <Square className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
                  {speech.listening ? 'Stop' : 'Detta'}
                </Button>
              ) : (
                <Badge variant="outline" className="text-xs">Dettatura non supportata: digita il testo</Badge>
              )}
              <Button variant="ghost" size="sm" onClick={() => { setTranscript(''); speech.reset(); }} disabled={!transcript} data-testid="clinical-transcript-reset">
                <RotateCcw className="h-3.5 w-3.5" /> Pulisci
              </Button>
              {speech.listening && speech.interim && (
                <span className="text-xs text-muted-foreground italic truncate flex-1" data-testid="clinical-interim">“{speech.interim}”</span>
              )}
            </div>
            <Textarea
              value={transcript}
              onChange={(e) => setTranscript(e.target.value)}
              rows={4}
              placeholder="Premi Detta e parla: «Il paziente riferisce cefalea da tre giorni. Obiettivo: PA 130/85. Diagnosi: cefalea tensiva. Piano: paracetamolo 1 g, controllo in 7 giorni.»"
              className={cn('text-sm rounded-xl', speech.listening && 'ring-2 ring-destructive/40')}
              data-testid="clinical-transcript"
            />
          </section>

          {/* Step 2 — AI locale streaming */}
          <section className="space-y-2" data-testid="clinical-ai">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">2 · Genera SOAP con AI locale</Label>
              <div className="flex items-center gap-2">
                {generating ? (
                  <Button variant="outline" size="sm" onClick={cancelGenerate} data-testid="clinical-generate-cancel"><StopCircle className="h-4 w-4" /> Interrompi</Button>
                ) : (
                  <Button size="sm" onClick={generate} disabled={!transcript.trim()} data-testid="clinical-generate-btn">
                    <Sparkles className="h-4 w-4" /> Genera SOAP
                  </Button>
                )}
              </div>
            </div>
            {(generating || stream) && (
              <div
                ref={streamBoxRef}
                className="rounded-xl border border-primary/30 bg-primary/5 p-3 text-xs font-mono whitespace-pre-wrap max-h-40 overflow-y-auto scrollbar-thin animate-fade-slide"
                data-testid="clinical-stream"
                aria-live="polite"
              >
                <span className={cn(generating && 'stream-caret')}>{stream || ' '}</span>
              </div>
            )}
          </section>

          {/* Step 3 — SOAP fields */}
          <section className="space-y-2" data-testid="clinical-soap">
            <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">3 · Cartella SOAP (modificabile)</Label>
            <div className="grid gap-2 sm:grid-cols-2">
              {SOAP_FIELDS.map((f) => (
                <div key={f.key} className={cn('rounded-xl border p-2.5 transition-colors', soap[f.key] ? f.color : 'border-border/70 bg-card')}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="h-6 w-6 rounded-lg bg-background/70 flex items-center justify-center font-display font-bold text-xs">{f.letter}</span>
                    <span className="text-xs font-semibold">{f.label}</span>
                    <span className="text-[10px] text-muted-foreground ml-auto hidden sm:inline">{f.hint}</span>
                  </div>
                  <Textarea
                    value={soap[f.key]}
                    onChange={(e) => setSoap({ ...soap, [f.key]: e.target.value })}
                    rows={3}
                    className="text-sm bg-background/60 rounded-lg border-transparent focus-visible:border-input"
                    placeholder={f.hint}
                    data-testid={`clinical-soap-${f.key}`}
                  />
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap pt-1">
              <Button variant="outline" size="sm" onClick={exportFhir} disabled={!hasSoap || exporting} data-testid="clinical-export-fhir">
                <FileDown className="h-4 w-4" /> {exporting ? 'Export…' : 'FHIR R4'}
              </Button>
              <Button onClick={save} disabled={!hasSoap || saving} className="touch-target min-w-[160px]" data-testid="clinical-save-btn">
                <Save className="h-4 w-4" /> {saving ? 'Salvataggio…' : 'Salva cartella'}
              </Button>
            </div>
          </section>

          <ClinicalNotesHistory history={history} loading={historyLoading} />
        </div>
      </ScrollArea>
    </div>
  );
};

export default ClinicalRecordPanel;
